import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NodeProps, useViewport } from 'reactflow';
import styled from 'styled-components';

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const LabelContainer = styled.div<{ selected: boolean; $color?: string }>`
  cursor: default;
  font-size: 12px;
  color: #3c4043;
  line-height: 1.4;
  padding: 2px 4px;
  border-radius: 3px;
  border: 1px solid ${props => (props.selected ? '#1a73e8' : (props.$color || 'transparent'))};
  background: ${props => props.$color ? `${props.$color}18` : 'transparent'};
  outline: none;
  min-width: 24px;
  max-width: 260px;
  white-space: pre-wrap;
  word-break: break-word;
  transition: border-color 0.15s ease, background 0.15s ease;
`;

const CommentContainer = styled.div<{ selected: boolean; $color?: string }>`
  position: relative;
  cursor: default;
  font-size: 12px;
  color: #3c4043;
  line-height: 1.5;
  background: ${props => props.$color ? `${props.$color}15` : '#fffde7'};
  border: 1px solid ${props => (props.selected ? '#1a73e8' : (props.$color || '#e8eaed'))};
  border-radius: 4px;
  padding: 8px 10px;
  min-width: 60px;
  min-height: 30px;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  overflow: auto;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  /* Corner fold */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 10px;
    background: linear-gradient(
      225deg,
      #f1f3f4 0%,
      #f1f3f4 50%,
      ${props => props.$color ? `${props.$color}30` : '#f5f0c0'} 50%,
      ${props => props.$color ? `${props.$color}30` : '#f5f0c0'} 100%
    );
    border-bottom-left-radius: 2px;
    border-top-right-radius: 4px;
  }
`;

const EditArea = styled.textarea`
  font-family: inherit;
  font-size: 12px;
  line-height: 1.4;
  color: inherit;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  padding: 0;
  margin: 0;
  width: 100%;
  min-height: 18px;
  overflow: hidden;
`;

const DisplayText = styled.div`
  min-height: 14px;
`;

const ResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  cursor: nwse-resize;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: 2px;
    right: 2px;
    width: 6px;
    height: 6px;
    border-right: 2px solid #bdc1c6;
    border-bottom: 2px solid #bdc1c6;
  }
`;

/* ------------------------------------------------------------------ */
/*  Node data interface                                                */
/* ------------------------------------------------------------------ */

interface AnnotationData {
  text: string;
  annotationType: 'label' | 'comment';
  color?: string;
  width?: number;
  height?: number;
  onTextChange?: (id: string, text: string) => void;
  onSizeChange?: (id: string, width: number, height: number) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const AnnotationNode: React.FC<NodeProps<AnnotationData>> = ({
  id,
  data,
  selected,
}) => {
  const { text, annotationType, color, width, height, onTextChange, onSizeChange } = data;
  const viewport = useViewport();
  const zoomRef = useRef(viewport.zoom);
  zoomRef.current = viewport.zoom;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0, w: 0, h: 0 });

  // Keep draft in sync when the prop changes while not editing
  useEffect(() => {
    if (!editing) {
      setDraft(text);
    }
  }, [text, editing]);

  // Auto-focus and auto-size the textarea when entering edit mode
  useEffect(() => {
    if (editing && textareaRef.current) {
      const ta = textareaRef.current;
      ta.focus();
      ta.selectionStart = ta.value.length;
      ta.selectionEnd = ta.value.length;
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    }
  }, [editing]);

  const commitEdit = useCallback(() => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== text && onTextChange) {
      onTextChange(id, trimmed || text); // fall back to old text if empty
    }
    if (!trimmed) {
      setDraft(text);
    }
  }, [draft, text, id, onTextChange]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(true);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        setDraft(text);
        setEditing(false);
      } else if (e.key === 'Enter' && e.shiftKey) {
        // Shift+Enter commits the edit
        e.preventDefault();
        commitEdit();
      }
      // Plain Enter inserts a newline (default textarea behavior)
    },
    [text, commitEdit]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDraft(e.target.value);
      // Auto-resize
      const ta = e.target;
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    },
    []
  );

  // Resize handlers for comment boxes
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = true;
    const el = containerRef.current;
    startRef.current = {
      x: e.clientX,
      y: e.clientY,
      w: el ? el.offsetWidth : (width || 200),
      h: el ? el.offsetHeight : (height || 60),
    };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const zoom = zoomRef.current || 1;
      const dx = (ev.clientX - startRef.current.x) / zoom;
      const dy = (ev.clientY - startRef.current.y) / zoom;
      const newW = Math.max(60, startRef.current.w + dx);
      const newH = Math.max(30, startRef.current.h + dy);
      if (containerRef.current) {
        containerRef.current.style.width = `${newW}px`;
        containerRef.current.style.height = `${newH}px`;
      }
    };

    const handleMouseUp = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      const zoom = zoomRef.current || 1;
      const dx = (ev.clientX - startRef.current.x) / zoom;
      const dy = (ev.clientY - startRef.current.y) / zoom;
      const finalW = Math.max(60, startRef.current.w + dx);
      const finalH = Math.max(30, startRef.current.h + dy);
      if (onSizeChange) {
        onSizeChange(id, finalW, finalH);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [id, width, height, onSizeChange]);

  const content = editing ? (
    <EditArea
      ref={textareaRef}
      value={draft}
      onChange={handleChange}
      onBlur={commitEdit}
      onKeyDown={handleKeyDown}
      rows={1}
    />
  ) : (
    <DisplayText>{text || 'Double-click to edit'}</DisplayText>
  );

  if (annotationType !== 'comment') {
    return (
      <LabelContainer
        selected={selected || false}
        $color={color}
        onDoubleClick={handleDoubleClick}
        className={editing ? 'nodrag' : ''}
        style={{ cursor: editing ? 'text' : 'grab' }}
      >
        <div className={editing ? 'nodrag nowheel' : ''}>{content}</div>
      </LabelContainer>
    );
  }

  const commentStyle: React.CSSProperties = {
    cursor: editing ? 'text' : 'grab',
  };
  if (width) commentStyle.width = width;
  if (height) commentStyle.height = height;

  return (
    <CommentContainer
      ref={containerRef}
      selected={selected || false}
      $color={color}
      onDoubleClick={handleDoubleClick}
      className={editing || resizingRef.current ? 'nodrag' : ''}
      style={commentStyle}
    >
      <div className={editing ? 'nodrag nowheel' : ''}>{content}</div>
      <ResizeHandle
        className="nodrag"
        onMouseDown={handleResizeMouseDown}
      />
    </CommentContainer>
  );
};

export default AnnotationNode;
